import type { ControllerState } from '../map/controller'
import {
  DEFAULT_ACTIVE,
  getLayerDef,
  LAYERS,
  type LayerDef,
  type Timeline,
} from '../map/layers/registry'
import { prefs, setLayerOpacity, setLayers } from './prefs.svelte'

/**
 * The UI's view of the overlay stack. `LayerController` owns the truth;
 * `MapView.svelte` pushes this into it and writes the controller's answers
 * (the time axis, the loading flag) back here.
 *
 * Note that assigning an object to `$state` stores a *proxy*, so `layers.times`
 * read back out is never the array that went in. Nothing here may compare frame
 * arrays by identity; `MapView` tracks staleness with a derived key instead.
 */

export const layers = $state({
  /** Bottom-first stack order; the last id draws on top. */
  active: [] as string[],
  opacity: {} as Record<string, number>,
  times: [] as Date[],
  index: 0,
  /** The last observed frame when a forecast follows it; see the controller. */
  nowIndex: null as number | null,
  playing: false,
  loading: false,
})

/** Read the persisted stack at boot. `loadPrefs()` must have run first. */
export function initLayers(): void {
  appliedAxis = ''
  // Through the persisted stack as well: a browser that stored radar and an
  // HRRR field together — which it could, before the two clocks were a rule —
  // must not boot back into the mixed slider the rule exists to prevent.
  layers.active = onlyOneTimeline(
    prefs.layers.length > 0 ? [...prefs.layers] : [...DEFAULT_ACTIVE],
  )
  layers.opacity = Object.fromEntries(
    LAYERS.map((l) => [l.id, prefs.layerOpacity[l.id] ?? l.defaultOpacity]),
  )
}

export function opacityOf(id: string): number {
  return layers.opacity[id] ?? getLayerDef(id)?.defaultOpacity ?? 1
}

export function isActive(id: string): boolean {
  return layers.active.includes(id)
}

/**
 * Turning on an exclusive layer turns off the rest of its group: two radar
 * mosaics stacked on each other is noise, not information.
 *
 * Turning on a layer also turns off everything on the *other* clock. A model
 * run and a radar loop put their frames on one slider, and the union of a
 * 48-minute loop and a 48-hour forecast is a control that animates neither —
 * see `Timeline` in the registry. Layers with no frames (alerts, the SPC
 * products) are on no clock and survive the switch.
 */
export function toggleLayer(id: string): void {
  const def = getLayerDef(id)
  if (!def) return
  const turningOn = !layers.active.includes(id)
  const next = layers.active.filter(
    (other) =>
      other !== id &&
      !(def.exclusive && sameExclusiveGroup(def, other)) &&
      !(turningOn && onOtherTimeline(def.timeline, other)),
  )
  if (turningOn) next.push(id)
  commit(next)
}

/** The layer menu's "Turn all off". Opacities are kept for next time. */
export function clearLayers(): void {
  commit([])
}

/** Move a layer one step up (towards the top of the stack) or down. */
export function moveLayer(id: string, delta: number): void {
  const from = layers.active.indexOf(id)
  if (from < 0) return
  const to = from + delta
  if (to < 0 || to >= layers.active.length) return
  const next = [...layers.active]
  next.splice(to, 0, ...next.splice(from, 1))
  commit(next)
}

export function setOpacity(id: string, opacity: number): void {
  const clamped = Math.max(0, Math.min(1, opacity))
  layers.opacity = { ...layers.opacity, [id]: clamped }
  setLayerOpacity(id, clamped)
}

export function setIndex(i: number): void {
  layers.index = i
}

export function setPlaying(playing: boolean): void {
  layers.playing = playing
}

export function togglePlaying(): void {
  layers.playing = !layers.playing
}

/** Cheap identity for a frame axis. See the proxy note at the top of the file. */
function axisKey(times: readonly Date[]): string {
  if (times.length === 0) return ''
  return `${times.length}:${times[0].getTime()}:${times[times.length - 1].getTime()}`
}

let appliedAxis = ''

/** Called by the controller's `onChange`; never sets `active` or `opacity`. */
export function applyControllerState(s: ControllerState): void {
  // The play loop emits once per frame. Re-assigning the array each time would
  // hand every reader a fresh proxy twice a second and re-run their $derived
  // work for an axis that has not moved.
  const key = axisKey(s.times)
  if (key !== appliedAxis) {
    appliedAxis = key
    layers.times = s.times
  }
  layers.index = s.index
  layers.nowIndex = s.nowIndex
  layers.loading = s.loading
  // The controller pauses itself when the axis empties; the UI has to notice.
  layers.playing = s.playing
}

function sameExclusiveGroup(def: LayerDef, otherId: string): boolean {
  const other = getLayerDef(otherId)
  return !!other && other.exclusive && other.group === def.group
}

/** True when `otherId` has frames and they are on a different clock. */
function onOtherTimeline(timeline: Timeline | null, otherId: string): boolean {
  if (!timeline) return false
  const other = getLayerDef(otherId)?.timeline ?? null
  return other !== null && other !== timeline
}

/**
 * Keep the topmost timed layer's clock and drop the layers on the other one.
 *
 * Topmost, because the stack is bottom-first and the last id draws on top:
 * that is the layer someone turned on most recently and the one they are
 * looking at.
 */
function onlyOneTimeline(ids: string[]): string[] {
  const timeline = [...ids].reverse().map((id) => getLayerDef(id)?.timeline).find(Boolean) ?? null
  return ids.filter((id) => !onOtherTimeline(timeline, id))
}

function commit(next: string[]): void {
  layers.active = next
  setLayers(next)
}
